import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Plus,
  Pencil,
  Trash2,
  RefreshCcw,
  Search,
  Inbox,
  X,
  Eye,
  EyeOff,
  Loader2,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { authApi, formatApiError } from "@/lib/auth";

const empty = {
  title: "",
  location: "",
  type: "Full-time",
  team: "",
  description: "",
  active: true,
};

export default function JobsManager() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null); // null | 'new' | job
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [errors, setErrors] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await authApi.get("/admin/jobs");
      setJobs(data || []);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Failed to load jobs.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    if (!q.trim()) return jobs;
    const t = q.toLowerCase();
    return jobs.filter((j) =>
      [j.title, j.location, j.type, j.team]
        .filter(Boolean)
        .some((v) => v.toLowerCase().includes(t))
    );
  }, [jobs, q]);

  const openNew = () => {
    setForm(empty);
    setErrors({});
    setEditing("new");
  };
  const openEdit = (j) => {
    setForm({
      title: j.title || "",
      location: j.location || "",
      type: j.type || "Full-time",
      team: j.team || "",
      description: j.description || "",
      active: !!j.active,
    });
    setErrors({});
    setEditing(j);
  };
  const closeForm = () => {
    setEditing(null);
    setErrors({});
  };

  const validate = () => {
    const e = {};
    if (!form.title || form.title.trim().length < 2) e.title = "Enter a job title.";
    if (!form.location || form.location.trim().length < 2) e.location = "Enter a location.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const save = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        location: form.location.trim(),
        type: (form.type || "Full-time").trim(),
        team: form.team?.trim() || null,
        description: form.description?.trim() || null,
        active: !!form.active,
      };
      if (editing === "new") {
        const { data } = await authApi.post("/admin/jobs", payload);
        setJobs((prev) => [data, ...prev]);
        toast.success("Job posted.");
      } else {
        const { data } = await authApi.put(`/admin/jobs/${editing.id}`, payload);
        setJobs((prev) => prev.map((j) => (j.id === data.id ? data : j)));
        toast.success("Job updated.");
      }
      closeForm();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    if (!toDelete) return;
    try {
      await authApi.delete(`/admin/jobs/${toDelete.id}`);
      setJobs((prev) => prev.filter((j) => j.id !== toDelete.id));
      toast.success("Job removed.");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Delete failed.");
    } finally {
      setToDelete(null);
    }
  };

  const toggleActive = async (j) => {
    try {
      const { data } = await authApi.put(`/admin/jobs/${j.id}`, { active: !j.active });
      setJobs((prev) => prev.map((x) => (x.id === data.id ? data : x)));
      toast.success(data.active ? "Job is now visible on Careers page." : "Job is now hidden.");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Update failed.");
    }
  };

  const activeCount = jobs.filter((j) => j.active).length;

  return (
    <div data-testid="jobs-manager">
      {/* Stats + toolbar */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8">
        <div className="bg-white border border-hairline p-6">
          <div className="font-display text-4xl font-medium text-forest">{jobs.length}</div>
          <div className="text-xs uppercase tracking-[0.2em] text-muted2 mt-2">Total roles</div>
        </div>
        <div className="bg-white border border-hairline p-6">
          <div className="font-display text-4xl font-medium text-forest">{activeCount}</div>
          <div className="text-xs uppercase tracking-[0.2em] text-muted2 mt-2">Visible on site</div>
        </div>
        <div className="bg-white border border-hairline p-6 hidden md:block">
          <div className="font-display text-4xl font-medium text-forest">{jobs.length - activeCount}</div>
          <div className="text-xs uppercase tracking-[0.2em] text-muted2 mt-2">Hidden</div>
        </div>
      </div>

      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-4">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted2" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search title, location, team..."
            data-testid="jobs-search"
            className="pl-9 h-11 border-hairline focus-visible:ring-2 focus-visible:ring-forest"
          />
        </div>
        <div className="flex gap-2 self-start">
          <button
            onClick={load}
            data-testid="jobs-refresh"
            className="inline-flex items-center gap-2 text-sm text-ink border border-hairline px-4 py-2.5 hover:bg-ink hover:text-bone transition-colors"
          >
            <RefreshCcw className="w-4 h-4" /> Refresh
          </button>
          <button
            onClick={openNew}
            data-testid="jobs-new"
            className="btn-primary"
          >
            <Plus className="w-4 h-4" /> New role
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-hairline overflow-hidden">
        {loading ? (
          <div data-testid="jobs-loading" className="p-12 text-center text-muted2">Loading…</div>
        ) : filtered.length === 0 ? (
          <div data-testid="jobs-empty" className="p-16 text-center">
            <Inbox className="w-10 h-10 mx-auto text-muted2 mb-4" strokeWidth={1.3} />
            <div className="font-display text-xl text-ink">
              {jobs.length === 0 ? "No jobs yet" : "No results"}
            </div>
            <p className="text-sm text-muted2 mt-2">
              {jobs.length === 0
                ? "Post your first opening — it will show on the Careers page instantly."
                : "No jobs match your search."}
            </p>
            {jobs.length === 0 && (
              <button onClick={openNew} className="btn-primary mt-6 inline-flex">
                <Plus className="w-4 h-4" /> Post a role
              </button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-bone border-b border-hairline">
                <tr className="text-left">
                  <th className="px-5 py-3 font-medium text-xs uppercase tracking-[0.16em] text-muted2">Title</th>
                  <th className="px-5 py-3 font-medium text-xs uppercase tracking-[0.16em] text-muted2">Location</th>
                  <th className="px-5 py-3 font-medium text-xs uppercase tracking-[0.16em] text-muted2">Team</th>
                  <th className="px-5 py-3 font-medium text-xs uppercase tracking-[0.16em] text-muted2">Status</th>
                  <th className="px-5 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((j) => (
                  <tr key={j.id} data-testid={`job-row-${j.id}`} className="border-b border-hairline last:border-b-0 hover:bg-bone/60">
                    <td className="px-5 py-4">
                      <div className="font-medium text-ink">{j.title}</div>
                      <div className="text-xs text-muted2 mt-0.5">{j.type}</div>
                    </td>
                    <td className="px-5 py-4 text-ink/80">{j.location}</td>
                    <td className="px-5 py-4 text-ink/80">{j.team || "—"}</td>
                    <td className="px-5 py-4">
                      <button
                        onClick={() => toggleActive(j)}
                        data-testid={`toggle-${j.id}`}
                        className={`inline-flex items-center gap-1.5 px-2 py-1 text-xs border ${
                          j.active
                            ? "bg-forest/8 text-forest border-forest/25"
                            : "bg-muted text-muted2 border-hairline"
                        }`}
                      >
                        {j.active ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                        {j.active ? "Live" : "Hidden"}
                      </button>
                    </td>
                    <td className="px-5 py-4 text-right whitespace-nowrap">
                      <button
                        onClick={() => openEdit(j)}
                        data-testid={`edit-${j.id}`}
                        className="text-muted2 hover:text-forest p-2"
                        aria-label="Edit"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setToDelete(j)}
                        data-testid={`delete-${j.id}`}
                        className="text-muted2 hover:text-red-600 p-2"
                        aria-label="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Editor dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && closeForm()}>
        <DialogContent data-testid="job-dialog" className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">
              {editing === "new" ? "New role" : "Edit role"}
            </DialogTitle>
            <DialogDescription>
              This will show on the public Careers page instantly.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-[0.2em] text-muted2">Title*</Label>
              <Input
                data-testid="job-title"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="Senior Recruitment Consultant"
                className="mt-2 h-11 border-hairline focus-visible:ring-2 focus-visible:ring-forest"
              />
              {errors.title && <p className="mt-1 text-xs text-red-600">{errors.title}</p>}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-[0.2em] text-muted2">Location*</Label>
                <Input
                  data-testid="job-location"
                  value={form.location}
                  onChange={(e) => setForm({ ...form, location: e.target.value })}
                  placeholder="Ahmedabad, IN"
                  className="mt-2 h-11 border-hairline focus-visible:ring-2 focus-visible:ring-forest"
                />
                {errors.location && <p className="mt-1 text-xs text-red-600">{errors.location}</p>}
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.2em] text-muted2">Employment type</Label>
                <Input
                  data-testid="job-type"
                  value={form.type}
                  onChange={(e) => setForm({ ...form, type: e.target.value })}
                  placeholder="Full-time"
                  className="mt-2 h-11 border-hairline focus-visible:ring-2 focus-visible:ring-forest"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.2em] text-muted2">Team / department</Label>
              <Input
                data-testid="job-team"
                value={form.team}
                onChange={(e) => setForm({ ...form, team: e.target.value })}
                placeholder="Permanent Staffing"
                className="mt-2 h-11 border-hairline focus-visible:ring-2 focus-visible:ring-forest"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.2em] text-muted2">Description (optional)</Label>
              <Textarea
                data-testid="job-description"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Role summary, responsibilities, requirements..."
                rows={5}
                className="mt-2 border-hairline focus-visible:ring-2 focus-visible:ring-forest resize-none"
              />
            </div>
            <div className="flex items-center justify-between pt-2">
              <div>
                <Label className="text-sm">Show on Careers page</Label>
                <p className="text-xs text-muted2 mt-1">Turn off to hide without deleting.</p>
              </div>
              <Switch
                checked={form.active}
                onCheckedChange={(v) => setForm({ ...form, active: v })}
                data-testid="job-active"
              />
            </div>
          </div>

          <DialogFooter>
            <button
              onClick={closeForm}
              data-testid="job-cancel"
              className="btn-ghost"
              disabled={saving}
            >
              Cancel
            </button>
            <button
              onClick={save}
              data-testid="job-save"
              disabled={saving}
              className="btn-primary disabled:opacity-70"
            >
              {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving</> : editing === "new" ? "Post role" : "Save changes"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent data-testid="job-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this role?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove <strong>{toDelete?.title}</strong>. If you just
              want to take it off the Careers page temporarily, toggle it to Hidden instead.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="job-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={doDelete}
              data-testid="job-delete-confirm"
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
